import {
  getEventPayments,
  getPendingEventPayments,
  updateTimestampRFPSent,
  updateTimestampRFPSentFailed,
} from "@/lib/database/client/database";
import { editHotelCandidate } from "@/lib/database/client/database_admin";
import {
  notifyCustomerEmailSent,
  notifyHyattSlackFailed,
  notifyRfpAutoDraftFailed,
  notifyRfpDraftUpdated,
  notifyRfpFailedVenues,
} from "@/lib/notifications/admin-ops-notify-actions";
import { Events } from "@/lib/database/events";
import { getClientFlowPath } from "@/lib/events/client-flow-path";
import { isTestEvent } from "@/lib/events/test-event";
import { DASHBOARD_PATH } from "@/lib/routes/enterprise-nav-paths";
import { type SlackBlock, sendRichSlackMessageServerOnly } from "@/lib/notifications/rich-slack";
import { formatSlackMessage } from "@/lib/notifications/slack";
import { escapeSlackText, getEventVaSlackMention } from "@/lib/notifications/slack-mentions";
import { Payment } from "@/lib/database/payments";
import { StaticVenue } from "@/lib/database/venues";
import { regenerateRfpDraftAction, resolveCurrentRfpDraftAction } from "@/lib/rfp/draft-actions";
import { getCurrentUser } from "@/lib/db/users";
import { OeeQuote } from "@/features/ava-add-quotes/_logic/types";
import { VenueOption } from "@/lib/db/venue_options";
import { PaymentLinkData } from "@/lib/payments/types";
import { CustomerDecisionStatus, QuoteStatus, SlackNotificationType, VenueType } from "@/lib/shared/enums";
import { HotelChainRep } from "@/lib/types/hotel";
import { getPreferredChainRep } from "@/lib/db/hotel-chain-reps";
import {
  applyEnterpriseRepPrecedence,
  EnterpriseRepPreferences,
  NO_ENTERPRISE_REPS,
} from "@/lib/db/hotel-chain-rep-precedence";
import { buildChainRepMessageHtml } from "@/lib/email/utils/chain-rep-message";
import { sendTableEmail, type EmailAttachment, type TableEmailPayload } from "@/lib/email/send-table-email";
import { selectChainRepFallbackRecipients } from "@/lib/email/utils/chain-rep-recipients";
import { buildMissingVenueEmailSentence } from "@/lib/notifications/slack-text";
import { formatCityState } from "@/lib/utils/location";
import { Users } from "@/lib/users/types";
import {
  formatAccommodationsForSlack,
  getPeakRoomCount,
  normalizeAccommodationsToArray,
} from "@/lib/utils/accommodations";
import { sortAgendaChronologically } from "@/lib/utils/agenda-sort";
import { isValidEmail } from "@/features/forms/_logic/recipient-utils";
import { stripHtmlTags } from "@/lib/utils/sanitization";
import { getEventDateRange, getPreferredEventDates } from "@/lib/utils/event-dates";
import { getMeetingRoomNotes } from "@/lib/utils/event-requirements";
import { StaticVenueWithMetadata } from "@/lib/venues/types";
import { getEnterpriseRepPreferences } from "@/features/organization-settings/_logic/get-enterprise-rep-preferences";
import { resolveEnterpriseOrgId } from "@/lib/enterprise/resolve-enterprise-org";
import { isNorthstar } from "@/utils/env-utils";
import { getEnterpriseSlugFromUrl } from "@/utils/enterprise-theme";
import { fetchGETNext, fetchPOSTNext } from "@/utils/fetch-utils";
import { fetchVenueDeepLink } from "@/utils/url-utils";
import { SupabaseClient } from "@supabase/supabase-js";
import moment from "moment";
import { NEXT_BODY_SUFFIX } from "next/dist/lib/constants";
import { MAX_REP_EMAILS } from "@/features/enterprise-sourcing-search/_logic/rfp/constants";
import { flagHandoffCandidate } from "@/features/enterprise-sourcing-search/_logic/rfp/build-handoff-candidate-fields";
import { selectHeadlineQuote } from "@/lib/db/quotes";

export const PLACEHOLDER_TAX = "Enter a decimal (e.g. 7%)";
export const PLACEHOLDER_DECIMAL = "Enter a decimal";
export const PLACEHOLDER_POSITIVE_INTEGER = "Enter a whole number";
export const OLD_NOWADAYS_SOURCING_FEE = 0.05;
export const NOWADAYS_SOURCING_FEE = 0.07;
export const CREDIT_CARD_PROCESSING_FEE = 0.029;
export const IS_NORTHSTAR = isNorthstar;
export const IS_GOOGLE = getEnterpriseSlugFromUrl() === "google" || process.env.NEXT_PUBLIC_IS_GOOGLE === "true";
export const IS_DEMO = true;

/** Slack user id for Alexis (Hyatt national accounts) — pinged in every Hyatt RFP notification. */
export const ALEXIS_SLACK_USER_ID = "U08S3CKL726";
export const HYATT_CHAIN_NAME = "Hyatt";

/**
 * Deterministically selects venue indices for "Preferred by Google" tag.
 * Uses a hash of venue IDs to ensure consistent selection across refreshes.
 * @param venueIds - Array of venue IDs (event_venue_candidate_id or venue_static_id)
 * @param maxIndex - Maximum index to consider (e.g., first 5 venues)
 * @param count - Number of venues to select (default: 2-3 based on venue count)
 * @returns Set of indices that should show the "Preferred by Google" tag
 */
export function getGooglePreferredIndices(venueIds: number[], maxIndex: number, count?: number): Set<number> {
  if (venueIds.length === 0 || maxIndex === 0) {
    return new Set();
  }

  const actualMaxIndex = Math.min(maxIndex, venueIds.length);

  // Create a simple hash function for deterministic selection
  const hash = (id: number): number => {
    const str = id.toString();
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  // Deterministically determine count (2 or 3) based on hash of venue IDs
  const actualCount =
    count ??
    (() => {
      if (actualMaxIndex === 0) return 0;
      if (actualMaxIndex === 1) return 1;
      // Use hash of first venue ID to deterministically pick 2 or 3
      const firstVenueHash = hash(venueIds[0]);
      const deterministicCount = (firstVenueHash % 2) + 2; // 2 or 3
      return Math.min(deterministicCount, actualMaxIndex);
    })();

  // Get the first actualMaxIndex venue IDs and their hashes
  const venueHashes = venueIds.slice(0, actualMaxIndex).map((id, index) => ({
    index,
    id,
    hash: hash(id),
  }));

  // Sort by hash value (deterministic) and take the first actualCount
  venueHashes.sort((a, b) => {
    if (a.hash !== b.hash) {
      return a.hash - b.hash;
    }
    // If hashes are equal, use ID as tiebreaker
    return a.id - b.id;
  });

  const selectedIndices = new Set<number>();
  for (let i = 0; i < Math.min(actualCount, venueHashes.length); i++) {
    selectedIndices.add(venueHashes[i].index);
  }

  return selectedIndices;
}

export interface EventsProps {
  event: Events;
}

export type FilterState = {
  starRatings: number[];
  tripadvisorStarRatings: number[];
  locations: {
    filterText: string;
    condition: "city" | "city-state" | "custom-location-filter";
  }[];
  minPrice: number | null;
  maxPrice: number | null;
  liked: boolean | null;
  isNotLiked: boolean | null;
  sortOrder: "asc" | "desc" | null;
  sortBy: "name" | "price" | "star-rating" | null;
  sortDirection: "asc" | "desc";
  quoteStatus: QuoteStatus[];
  venueType: VenueType[];
  minMeetingRooms: number | null;
  maxMeetingRooms: number | null;
  rfpSent: boolean | null;
};

export const defaultFilterState: FilterState = {
  starRatings: [],
  tripadvisorStarRatings: [],
  locations: [],
  minPrice: null,
  maxPrice: null,
  liked: null,
  isNotLiked: null,
  sortOrder: null,
  sortBy: null,
  sortDirection: "asc",
  quoteStatus: [],
  venueType: [],
  minMeetingRooms: null,
  maxMeetingRooms: null,
  rfpSent: null,
};

export const venueTypeOptions = {
  [VenueType.HOTEL]: "Hotel",
  [VenueType.RESORT]: "Resort",
  [VenueType.RESTAURANT]: "Restaurant",
  [VenueType.BAR]: "Bar",
  [VenueType.NIGHTCLUB]: "Nightclub",
  [VenueType.EVENT_SPACE]: "Event Space",
  [VenueType.AIRBNB]: "Airbnb",
  [VenueType.AIRBNB_EXPERIENCES]: "Airbnb Experiences",
  [VenueType.AIRBNB_SERVICES]: "Airbnb Services",
  [VenueType.VACATION_RENTAL]: "Vacation Rental (VRBO, etc.)",
  [VenueType.OTHER]: "Other",
};

/**
 * Gets the average of the northstar and aaa ratings
 * @param hotel
 * @returns average of northstar or aaa rating, or 0 if none found
 */
export function getHotelStarRating(hotel: StaticVenue): number {
  if (hotel.northstar_rating) {
    if (hotel.aaa_rating) {
      return (hotel.northstar_rating + hotel.aaa_rating) / 2;
    } else {
      return hotel.northstar_rating;
    }
  } else {
    if (hotel.aaa_rating) {
      return hotel.aaa_rating;
    } else {
      return 0;
    }
  }
}

/**
 *
 * @param hotel
 * @returns tripadvisor rating, or 0 if none found
 */
export function getTripAdvisorStarRating(hotel: StaticVenue): number {
  if (hotel.tripadvisor?.tripadvisor_rating) {
    return hotel.tripadvisor.tripadvisor_rating;
  } else {
    return 0;
  }
}

export const getGoogleStarColor = (rating: number): string => {
  if (rating >= 4.5) return "text-emerald-400"; // soft green
  if (rating >= 4) return "text-green-300"; // lighter green
  if (rating >= 3) return "text-amber-300"; // mellow yellow
  if (rating >= 2) return "text-orange-300"; // gentle orange
  return "text-rose-300"; // soft red/pink
};

/**
 * format with dot and commas, two decimal points or zero
 *
 * Zero-decimal currencies (JPY, KRW, IDR, etc.) are detected via
 * `Intl.NumberFormat.resolvedOptions().maximumFractionDigits` and rendered
 * without any fractional part regardless of the `noDecimal` argument — those
 * currencies do not have a minor unit so `¥1,234.00` is incorrect.
 *
 * `Intl.NumberFormat` construction is ~50-200µs per call. Both the
 * zero-decimal lookup and the formatter itself are cached per
 * `(currency, noDecimal)` so chart tick formatters and table renderers don't
 * pay that cost on every render.
 */
const ZERO_DECIMAL_CURRENCY_CACHE = new Map<string, boolean>();
const CURRENCY_FORMATTER_CACHE = new Map<string, Intl.NumberFormat>();

function isZeroDecimalCurrency(currency: string): boolean {
  const cached = ZERO_DECIMAL_CURRENCY_CACHE.get(currency);
  if (cached !== undefined) return cached;
  const result =
    Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions().maximumFractionDigits === 0;
  ZERO_DECIMAL_CURRENCY_CACHE.set(currency, result);
  return result;
}

function getCurrencyFormatter(currency: string, noDecimal: boolean): Intl.NumberFormat {
  const key = `${currency}|${noDecimal ? 0 : 2}`;
  const cached = CURRENCY_FORMATTER_CACHE.get(key);
  if (cached) return cached;
  const formatter = Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: noDecimal ? 0 : 2,
    maximumFractionDigits: noDecimal ? 0 : 2,
  });
  CURRENCY_FORMATTER_CACHE.set(key, formatter);
  return formatter;
}

/**
 * Format a monetary value in the given currency.
 *
 * `Intl.NumberFormat` throws `RangeError` on a malformed currency code, and
 * some codes reaching here originate from AI-extracted quote data that is not
 * ISO-4217 validated. Rather than crash the caller (export builders, grid cell
 * renderers), fall back to `"<CODE> <amount>"` for unrecognized codes.
 *
 * @param currency - ISO 4217 currency code.
 * @param value - Amount to format.
 * @param noDecimal - Drop fractional digits (forced for zero-decimal currencies).
 * @returns The formatted string, or a `"<CODE> <amount>"` fallback.
 */
export function formatCurrency(currency: string, value: number, noDecimal?: boolean): string {
  try {
    const useNoDecimal = noDecimal || isZeroDecimalCurrency(currency);
    const rounded = useNoDecimal ? Math.round(value) : value;
    return getCurrencyFormatter(currency, useNoDecimal).format(rounded);
  } catch {
    const rounded = noDecimal ? Math.round(value) : value;
    return `${(currency || "").toUpperCase()} ${rounded.toLocaleString("en-US")}`.trim();
  }
}

/**
 * Resolves the URL a user should be redirected to for a given event, based on its
 * `event_status`. Drafts (or events with no status) go to the dashboard; known
 * statuses delegate to {@link getClientFlowPath}; unknown statuses fall back to home.
 *
 * @param event - The event to route, or null.
 * @returns The redirect path (`"/"` for null/unknown, {@link DASHBOARD_PATH} for drafts).
 */
export function getCurrentEventRedirect(event: Events | null): string {
  if (!event) {
    return "/";
  }

  // Handle draft events or events without status
  if (!event.event_status || (event as any).is_draft) {
    return DASHBOARD_PATH;
  }

  const path = getClientFlowPath(event.event_id, event.event_status);
  if (!path) {
    console.warn("Unknown event status:", event.event_status);
    return `/`; // Return home page as fallback instead of throwing
  }
  return path;
}

export async function sendSlackMessage(
  type: SlackNotificationType,
  title: string,
  message?: string,
  details?: { title: string; message: string }[]
): Promise<void> {
  await fetchPOSTNext(`/api/webhooks`, {
    type,
    message: title === "" && message && !details ? message : formatSlackMessage(title, message, details),
  });
}

export async function sendSlackMessageNoAuth(
  type: SlackNotificationType,
  title: string,
  message?: string,
  details?: { title: string; message: string }[]
): Promise<void> {
  await fetchPOSTNext(`/api/webhooks`, {
    type,
    message: formatSlackMessage(title, message, details),
  });
}

/**
 * Mapping of enterprise slugs to their Slack notification types.
 * Add new enterprises here as they get dedicated Slack channels.
 */
const ENTERPRISE_SLACK_CHANNELS: Record<string, SlackNotificationType> = {
  google: SlackNotificationType.GOOGLE,
  // Add more enterprises here as needed:
  // acme: SlackNotificationType.ACME,
  // stripe: SlackNotificationType.STRIPE,
};

/**
 * Gets the appropriate Slack notification type based on enterprise context.
 * Routes enterprise messages to their dedicated channel if configured.
 */
export function getSlackTypeForEnterprise(
  defaultType: SlackNotificationType,
  enterpriseSlug?: string | null
): SlackNotificationType {
  // Check environment variable override (for local development)
  if (IS_GOOGLE) {
    return SlackNotificationType.GOOGLE;
  }

  // Check if enterprise has a dedicated Slack channel
  if (enterpriseSlug && ENTERPRISE_SLACK_CHANNELS[enterpriseSlug]) {
    return ENTERPRISE_SLACK_CHANNELS[enterpriseSlug];
  }

  return defaultType;
}

/**
 * Sends a Slack message, routing to Google channel if in Google enterprise context.
 * Use this for event-related messages that should be separated by enterprise.
 */
export async function sendSlackMessageForEnterprise(
  defaultType: SlackNotificationType,
  title: string,
  message?: string,
  details?: { title: string; message: string }[],
  enterpriseSlug?: string | null
): Promise<void> {
  const type = getSlackTypeForEnterprise(defaultType, enterpriseSlug);
  await sendSlackMessage(type, title, message, details);
}

export async function getTotalPaymentsMadeForEvent(
  supabase: SupabaseClient,
  event_id: string
): Promise<{ payments: Payment[]; total: number } | null> {
  try {
    console.log(`CHECKING ALL PAYMENTS WITH EVENT ID: ${event_id}`);

    // Use the new database function to fetch payments
    const payments = await getEventPayments(supabase, parseInt(event_id));

    if (!payments) {
      return null;
    }

    // Calculate the total amount from all payments (captured amount minus refunds)
    const total = payments.reduce((acc, payment) => {
      const capturedAmount = parseFloat(payment.payment_amount_captured) || 0;
      const refundedAmount = parseFloat(payment.amount_refunded) || 0;
      return acc + (capturedAmount - refundedAmount);
    }, 0);

    // Map the payments to include any additional properties or formatting
    const formattedPayments = payments.map((payment) => ({
      ...payment,
      // Assuming payment.receipt_url is directly available from the payments data
      url: payment.receipt_url,
    }));

    return { payments: formattedPayments, total };
  } catch (error) {
    console.error("Error fetching payments:", error);
    return null;
  }
}

export async function getPendingPaymentsForEvent(
  supabase: SupabaseClient,
  event_id: string
): Promise<{ payments: Payment[]; total: number } | null> {
  console.log(`CHECKING ALL PENDING PAYMENTS WITH EVENT ID: ${event_id}`);

  try {
    // Use the new database function to fetch pending payments
    const payments = await getPendingEventPayments(supabase, parseInt(event_id));

    if (!payments) {
      return null;
    }

    // Map the payments to include any additional properties or formatting
    const formattedPayments = payments.map((payment) => ({
      ...payment,
      amount: parseFloat(payment.payment_amount_expected || "0"), // Default to 0 if not present
      created: payment.created_at || Math.floor(Date.now() / 1000).toString(), // Default to current Unix timestamp if not present
    }));

    // Calculate the total amount from all pending payments
    const total = formattedPayments.reduce((acc, payment) => acc + payment.amount, 0);

    return { payments: formattedPayments, total };
  } catch (error) {
    console.error("Error fetching pending payments:", error);
    return null;
  }
}

export async function fetchPaymentCharge(chargeId: string) {
  try {
    const response = await fetch(`/api/payment/fetch_payment_charge?charge_id=${chargeId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch charge");
    }
    console.log("THIS IS THE RESPONSE", response);
    const data = await response.json();
    return data.charge;
  } catch (error) {
    console.error("Error fetching payment charge:", error);
    return null;
  }
}

// Define the function to create the payment link
export async function createPaymentLink(data: PaymentLinkData) {
  const response = await fetchPOSTNext(`/api/payment/create_payment_link`, data);

  if (response.status !== 200) {
    return null;
  }

  return await response.json();
}

/**
 *
 * sorts the venues based on various factors
 *
 * accounts for optional quotes
 *
 * @returns a copy of the array, sorted
 *
 */
export function sortVenuesForCustomerView(options: VenueOption[], filterState: FilterState | null): VenueOption[] {
  return options.sort((a, b) => {
    // Move DECLINED to the bottom, SHORTLISTED to the top
    const decisionPriority = {
      [CustomerDecisionStatus.SHORTLISTED]: -1,
      [CustomerDecisionStatus.PENDING]: 0,
      [CustomerDecisionStatus.DECLINED]: 1,
      [CustomerDecisionStatus.REMOVED]: 2,
    };

    // Ensure the type of customer_decision_status is a valid key of decisionPriority
    const aDecisionPriority = decisionPriority[a.candidate.customer_decision_status as CustomerDecisionStatus] ?? 0;
    const bDecisionPriority = decisionPriority[b.candidate.customer_decision_status as CustomerDecisionStatus] ?? 0;

    if (aDecisionPriority !== bDecisionPriority) {
      return aDecisionPriority - bDecisionPriority;
    }

    // The newest priced offer this venue has live, else any priced quote at all.
    const hasTotal = (q: OeeQuote) => q.grand_total != null && q.grand_total !== 0;
    const getActiveQuote = (option: VenueOption) => {
      return selectHeadlineQuote(option.quotes, hasTotal) ?? option.quotes.find(hasTotal) ?? null;
    };

    const aActiveQuote = getActiveQuote(a);
    const bActiveQuote = getActiveQuote(b);

    // Sort by whether a quote exists - prioritize venues with non-zero grand_total first
    const aHasOeeQuote = aActiveQuote?.grand_total != null && aActiveQuote.grand_total !== 0;
    const bHasOeeQuote = bActiveQuote?.grand_total != null && bActiveQuote.grand_total !== 0;

    if (aHasOeeQuote && !bHasOeeQuote) {
      return -1;
    }
    if (!aHasOeeQuote && bHasOeeQuote) {
      return 1;
    }

    // Within the same quote group (both have quotes or both don't), sort by ranking
    const aHasAnyQuote = aHasOeeQuote;
    const bHasAnyQuote = bHasOeeQuote;

    if (aHasAnyQuote === bHasAnyQuote) {
      // Both in same quote group, sort by ranking
      const aHasRanking = a.candidate.ranking !== null && a.candidate.ranking !== undefined;
      const bHasRanking = b.candidate.ranking !== null && b.candidate.ranking !== undefined;

      if (aHasRanking && !bHasRanking) {
        return -1;
      }
      if (!aHasRanking && bHasRanking) {
        return 1;
      }
      if (aHasRanking && bHasRanking) {
        return a.candidate.ranking! - b.candidate.ranking!;
      }
    }

    // Prioritize venues with customer_facing_notes at the top
    const aHasCustomerFacingNotes =
      !!a.candidate.customer_facing_notes && a.candidate.customer_facing_notes.trim() !== "";
    const bHasCustomerFacingNotes =
      !!b.candidate.customer_facing_notes && b.candidate.customer_facing_notes.trim() !== "";

    if (aHasCustomerFacingNotes && !bHasCustomerFacingNotes) {
      return -1;
    }
    if (!aHasCustomerFacingNotes && bHasCustomerFacingNotes) {
      return 1;
    }

    // Push unavailable venues to the bottom
    const aIsUnavailable = a.candidate.is_unavailable;
    const bIsUnavailable = b.candidate.is_unavailable;

    if (!aIsUnavailable && bIsUnavailable) {
      return -1;
    }
    if (aIsUnavailable && !bIsUnavailable) {
      return 1;
    }

    // If filterState is null, skip the sorting logic that depends on it
    if (filterState) {
      // Sort by sortBy filter if set
      if (filterState.sortBy) {
        if (filterState.sortBy === "name") {
          const comparison = a.venue.venue_name.localeCompare(b.venue.venue_name);
          return filterState.sortDirection === "asc" ? comparison : -comparison;
        } else if (filterState.sortBy === "price") {
          const aPrice = a.candidate.estimated_nightly_rate_usd || 0;
          const bPrice = b.candidate.estimated_nightly_rate_usd || 0;
          const comparison = aPrice - bPrice;
          return filterState.sortDirection === "asc" ? comparison : -comparison;
        } else if (filterState.sortBy === "star-rating") {
          const aRating = getHotelStarRating(a.venue);
          const bRating = getHotelStarRating(b.venue);
          const comparison = bRating - aRating; // Higher ratings first by default
          return filterState.sortDirection === "asc" ? -comparison : comparison;
        }
      }

      // Sort by price based on sortOrder as the second priority
      if (filterState.sortOrder) {
        const priceA = aActiveQuote?.grand_total ?? 0;
        const priceB = bActiveQuote?.grand_total ?? 0;

        if (filterState.sortOrder === "asc") {
          return priceA - priceB;
        } else if (filterState.sortOrder === "desc") {
          return priceB - priceA;
        }
      }
    }

    // If both have quotes or neither have, then sort by marked_as_liked
    const aLiked = a.candidate.marked_as_liked;
    const bLiked = b.candidate.marked_as_liked;

    if (aLiked && !bLiked) {
      return -1;
    }
    if (!aLiked && bLiked) {
      return 1;
    }

    // Prioritize venues with images that will actually load
    // ImagePlaceholder shows when images.length === 0 (finalImages.length === 0)
    // finalImages is empty when: coverPhotoUrl is null AND imageUrls.length === 0
    // Since we can't check storage synchronously, check cover_photo_url which is what getWorkingImageUrl uses
    const aHasImages = !!a.venue.cover_photo_url;
    const bHasImages = !!b.venue.cover_photo_url;

    if (aHasImages && !bHasImages) {
      return -1;
    }
    if (!aHasImages && bHasImages) {
      return 1;
    }

    // If both have the same liked status, sort by ranking
    const aHasRanking = a.candidate.ranking !== null && a.candidate.ranking !== undefined;
    const bHasRanking = b.candidate.ranking !== null && b.candidate.ranking !== undefined;

    if (aHasRanking && !bHasRanking) {
      return -1;
    }
    if (!aHasRanking && bHasRanking) {
      return 1;
    }
    if (aHasRanking && bHasRanking) {
      return a.candidate.ranking! - b.candidate.ranking!;
    }

    // Sort by hotel star rating
    const ratingA = getHotelStarRating(a.venue);
    const ratingB = getHotelStarRating(b.venue);
    if (ratingA !== ratingB) {
      return ratingB - ratingA; // Sort in descending order
    }

    // Sort by Tripadvisor star rating before falling back to ID
    const aRating = getTripAdvisorStarRating(a.venue);
    const bRating = getTripAdvisorStarRating(b.venue);
    if (aRating !== bRating) {
      return bRating - aRating; // Sort in descending order
    }

    // Finally, sort by id if all else is equal
    return a.candidate.event_venue_candidate_id - b.candidate.event_venue_candidate_id;
  });
}

export async function addVenuesToEvent(
  supabase: SupabaseClient,
  event: Events,
  cities?: string[],
  min_num_rooms?: number,
  min_hotel_rating?: number,
  venue_static_ids?: number[],
  venue_type?: string,
  setLoadingText?: (text: string) => void,
  candidateSource?: string
): Promise<number> {
  // do one at a time so that we can get progress updates, when we are adding hotels
  console.log("addVenuesToEvent", venue_static_ids);
  console.log("event", event);
  console.log("cities", cities);
  console.log("min_num_rooms", min_num_rooms);
  console.log("min_hotel_rating", min_hotel_rating);
  console.log("venue_type", venue_type);
  console.log("candidateSource", candidateSource);

  const user = await getCurrentUser(supabase);
  if (venue_static_ids && setLoadingText) {
    let count = 0;
    for (const id of venue_static_ids) {
      console.log("addVenuesToEvent", id);
      setLoadingText(`Processing ID: ${id} (${Math.floor((count / venue_static_ids.length) * 100)}% complete)...`);
      const response = await fetchPOSTNext(`/api/venues/add_venues_to_event`, {
        event: event,
        cities: cities,
        min_num_rooms: min_num_rooms,
        min_hotel_rating: min_hotel_rating,
        venue_static_ids: [id],
        venue_type: venue_type,
        candidate_source: candidateSource,
        added_by_user_id: user?.user_id,
      });

      if (!response || response.status !== 200) {
        return 0;
      }
      count++;
    }
    setLoadingText(`Done loading IDs. Images will download in the background!`);
    return count;
  } else {
    const response = await fetchPOSTNext(`/api/venues/add_venues_to_event`, {
      event: event,
      cities: cities,
      min_num_rooms: min_num_rooms,
      min_hotel_rating: min_hotel_rating,
      venue_static_ids: venue_static_ids,
      venue_type: venue_type,
      candidate_source: candidateSource,
      added_by_user_id: user?.user_id,
    });

    if (!response || response.status !== 200) {
      return 0;
    }

    const data = await response.json();
    return data.venuesAdded || 0;
  }
}

/**
 * Posts a rich Slack card to the Fetch channel, @-mentioning the event's assigned VA, about venues
 * `sendRFPEmails` couldn't email normally — with a button that deep-links into the event's Fetch
 * venue workspace so the VA lands where they can act. `"no-contact"` venues have no email and no
 * chain rep, so the RFP couldn't go out and needs a contact found + manual submission;
 * `"chain-rep-fallback"` venues DID send (to their chain rep as TO) — a data-hygiene ask to track
 * down the venue's own direct email for future events, not a resend. No-ops (never throws) when
 * `venues` is empty. Routes to the code's assigned VA (ENG-303), `<!channel>` fallback when unset.
 *
 * @param venues - The venues to report; no-op if empty.
 * @param eventId - The event the RFP(s) were for — resolves the VA and the Fetch deep link.
 * @param kind - Which of the two distinct asks this ping represents.
 */
async function notifyVaAboutVenueContact(
  supabase: SupabaseClient,
  venues: StaticVenueWithMetadata[],
  eventId: number,
  kind: "no-contact" | "chain-rep-fallback"
): Promise<void> {
  if (venues.length === 0) return;

  try {
    const vaMention = await getEventVaSlackMention(supabase, eventId);
    const isPlural = venues.length > 1;
    const venueList = venues
      .map((venue) => `• ${escapeSlackText(venue.venue_name)} (${venue.venue_static_id})`)
      .join("\n");
    // Deep link into Fetch so the VA opens the event's venue workspace and can add the contact right
    // there. With exactly one venue, pre-select it (its contact pane); with several, stay
    // event-scoped since the card lists them all (the shared builder owns the origin + param
    // contract).
    const singleVenueStaticId = venues.length === 1 ? venues[0].venue_static_id : null;
    const fetchUrl = fetchVenueDeepLink(eventId, singleVenueStaticId);
    const fetchLink = `<${fetchUrl}|ev-${eventId} in Fetch>`;

    const title =
      kind === "no-contact"
        ? "RFP could not be sent — no contact on file"
        : "RFP sent via chain rep — add venue's direct email";

    const sentence =
      kind === "no-contact"
        ? `${vaMention} — the customer liked ${isPlural ? "these venues" : "this venue"} for ${fetchLink}, but we have no email on file, so the RFP couldn't go out. Please find a contact and submit ${isPlural ? "them" : "it"}.`
        : buildMissingVenueEmailSentence(isPlural ? "these hotels" : "this hotel", fetchLink, isPlural);

    const blocks: SlackBlock[] = [
      { type: "header", text: { type: "plain_text", text: title, emoji: true } },
      { type: "section", text: { type: "mrkdwn", text: sentence } },
      { type: "section", text: { type: "mrkdwn", text: venueList } },
      {
        type: "actions",
        elements: [{ type: "button", text: { type: "plain_text", text: "Open in Fetch", emoji: true }, url: fetchUrl }],
      },
    ];

    await sendRichSlackMessageServerOnly(SlackNotificationType.FETCH, `${vaMention} ${title}`, blocks);
  } catch (error) {
    console.error(`Error sending Slack message for ${kind} venues:`, error);
  }
}

/**
 * The `source` tag `sendRFPEmailsAutomatically` stamps on every RFP it sends. The automatic
 * customer-driven sends (portal like, event-creation) all flow through that wrapper, so this is the
 * single value the no-contact VA ping is suppressed for — deliberate admin / chain-rep sends carry
 * their own source and still ping. Kept as one const so the producer and the gate can't drift.
 */
const LIKED_VENUE_SOURCE = "liked-venue";

/**
 * Send RFP emails to a batch of venues. For each venue, resolves chain reps
 * and enterprise preferred reps to CC, generates chain-specific messaging
 * (e.g. Hilton/HWS preamble), and dispatches via the standard email API.
 * Skips Airbnb-family and VRBO venues. Venues with no email are returned in
 * `noEmailVenues` for separate handling instead of being sent.
 * @param supabase - Authenticated Supabase client
 * @param subject - Email subject line
 * @param draft - HTML body draft
 * @param venues - Venues to send to; airbnb/vrbo are skipped, no-email are returned
 * @param from - Sender email address
 * @param event - Event the RFP is for; supplies event_id and org_id
 * @param source - Originating UI surface (e.g. "admin-dash-rfp")
 * @param attachments - Optional base64-encoded attachments applied to each send
 * @param from_name - Optional display name for the sender
 * @param allowTestEvent - Whether to override the test-event send chokepoint
 * @param buildBody - Optional per-venue email body builder
 * @param chainRepLookupClient - Service-role client for resolving chain reps AND enterprise
 *   preferred reps, used only when `supabase` is a customer's authenticated session (which RLS
 *   blocks from seeing globally-shared chain reps at `org_id` 999, and blocks entirely from
 *   `enterprise_preferred_venues`/`enterprise_preferred_chains` — service_role only, no
 *   `authenticated` policy at all). Server-side callers acting on behalf of a customer should
 *   pass one; falls back to `supabase` when omitted — safe for staff-authenticated callers
 *   resolving chain reps (RLS grants admins full access there) and for callers that already
 *   pass a service-role client as `supabase`, but NOT sufficient on its own for enterprise
 *   preferred reps from a browser-authenticated client — see `resolveEnterpriseReps`.
 * @param resolveEnterpriseReps - Overrides how enterprise preferred reps are resolved.
 *   Needed when this is called from client-side code (no service-role client can ever exist in
 *   the browser) — pass `getEnterpriseRepPreferencesAction` (a Nowadays-admin-gated Server
 *   Action) in that case. Defaults to the direct DB lookup via `chainRepLookupClient ?? supabase`,
 *   which is correct for server-side callers.
 * @param handoffClientEmail - On a Northstar hand-off send, the requesting client's
 *   email to CC (same recipient promised in the hand-off body copy) — routed through
 *   the same `isValidEmail` filter as every other CC address. Also gates flagging each
 *   successfully-sent venue's candidate `connected_client_to_hotel` (see
 *   `flagHandoffCandidate`), so it's excluded from the staff sourcing worklist. Omit
 *   for a standard send.
 * @returns Per-batch counts and the list of skipped no-email venues. Per-venue send failures are counted in `failed`; this function does not throw.
 */
export async function sendRFPEmails(
  supabase: SupabaseClient,
  subject: string,
  draft: string,
  venues: StaticVenueWithMetadata[],
  from: string,
  event: Events,
  source: string,
  attachments?: EmailAttachment[],
  from_name?: string,
  allowTestEvent?: boolean,
  buildBody?: (venue: StaticVenueWithMetadata, chainReps: HotelChainRep[]) => Promise<string>,
  chainRepLookupClient?: SupabaseClient,
  // Default: direct DB lookup, matching the chain-rep pattern right below. Callers with no
  // service-role client available (browser-authenticated) must override this.
  resolveEnterpriseReps: (
    enterpriseOrgId: number,
    venueStaticId: number,
    chainId?: number | null,
    brandId?: number | null
  ) => Promise<EnterpriseRepPreferences> = (orgId, venueStaticId, chainId, brandId) =>
    getEnterpriseRepPreferences(chainRepLookupClient ?? supabase, orgId, venueStaticId, chainId, brandId),
  handoffClientEmail?: string | null
): Promise<{
  message: string;
  sent: number;
  failed: number;
  noEmailVenues: StaticVenueWithMetadata[];
}> {
  let sent = 0;
  let failed = 0;
  const noEmailVenues: StaticVenueWithMetadata[] = [];
  // Venues with no email of their own that DID send, via a chain rep fallback.
  // Josh still needs to track down the venue's own direct email for future
  // events, so these get a separate (non-blocking) Slack ping — distinct from
  // noEmailVenues, which are true dead-ends that need manual RFP submission.
  const chainRepFallbackVenues: StaticVenueWithMetadata[] = [];

  const event_id = event.event_id;
  const airbnbTypes: string[] = [VenueType.AIRBNB, VenueType.AIRBNB_EXPERIENCES, VenueType.AIRBNB_SERVICES];

  // Resolve enterprise org once for all venues (same event.org_id)
  const enterpriseOrgId = await resolveEnterpriseOrgId(supabase, event.org_id);

  for (const venue of venues) {
    // Skip Airbnb-family and VRBO venues
    if (airbnbTypes.includes(venue.venue_type) || venue.venue_type === VenueType.VRBO) {
      console.log(`Skipping RFP for ${venue.venue_type} venue: ${venue.venue_name}`);
      continue;
    }

    // Use chain_id directly from venue_static
    const chainId = venue.chain_id;
    const chainName = venue.chain_name;

    console.log("Processing venue for RFP:", {
      venueName: venue.venue_name,
      chainId: chainId,
      chainName: chainName,
      brandId: venue.brand_id,
      brandName: venue.brand_name,
    });

    // Get chain reps and enterprise preferred reps in parallel. Chain reps use
    // chainRepLookupClient when provided — see the JSDoc above on why a plain
    // customer session can silently miss globally-shared reps here.
    const [resolvedChainReps, enterpriseReps] = await Promise.all([
      chainId ? getPreferredChainRep(chainRepLookupClient ?? supabase, chainId, event.org_id) : Promise.resolve([]),
      enterpriseOrgId
        ? resolveEnterpriseReps(enterpriseOrgId, venue.venue_static_id, chainId, venue.brand_id)
        : Promise.resolve(NO_ENTERPRISE_REPS),
    ]);
    // An enterprise with its own rep for this venue/chain gets only that rep, not the global one
    // too, unless it opted into keeping ours.
    const { chainReps, replacementRepEmails } = applyEnterpriseRepPrecedence(resolvedChainReps, enterpriseReps);
    console.log("Chain reps found:", chainReps);

    const chainRepEmailSet = new Set(chainReps.map((r) => r.email?.toLowerCase()));
    const filteredEnterpriseRepEmails = enterpriseReps.emails.filter(
      (email) => email.toLowerCase() !== venue.email?.toLowerCase() && !chainRepEmailSet.has(email.toLowerCase())
    );

    // A venue with no email of its own still gets the RFP if we have a chain rep
    // to fall back to (handled downstream by /api/email/send_rfp_email) — only
    // route to the manual-outreach Slack ping when there's truly no contact.
    // Derived from the same helper the route uses to pick the actual TO, so this
    // classification can't drift from what actually gets sent.
    const hasChainRepFallback = selectChainRepFallbackRecipients(chainReps, replacementRepEmails).to.length > 0;
    if (!venue.email && !hasChainRepFallback) {
      noEmailVenues.push(venue);
      continue;
    }

    // When the venue itself has no email, the chain rep(s) become the TO
    // recipient (resolved downstream in /api/email/send_rfp_email) rather than a
    // CC — so the "we've cc'd our rep" aside is wrong here and must be suppressed.
    const chainRepsForMessage = venue.email ? chainReps : [];

    let htmlContent: string;
    if (buildBody) {
      // Caller supplies a fully-built email document per venue (e.g. the exact
      // /enterprise template, with the chain-rep note baked into its own slot).
      // Send it verbatim — no <div> wrapper and no chain-message insertion,
      // which would corrupt a full <!DOCTYPE html> document.
      htmlContent = await buildBody(venue, chainRepsForMessage);
    } else {
      htmlContent = `<div style="font-family: Arial, sans-serif;">${draft}`;
      const chainMessage = buildChainRepMessageHtml(chainRepsForMessage, chainName);
      if (chainMessage) {
        // Insert after the first *text* paragraph, skipping a leading banner/logo
        // image paragraph (the branded RFP template opens with one, so anchoring
        // on the literal first </p> would drop the note above the greeting).
        let insertPos = -1;
        const paragraphRegex = /<p\b[^>]*>[\s\S]*?<\/p>/gi;
        let match: RegExpExecArray | null;
        while ((match = paragraphRegex.exec(htmlContent)) !== null) {
          if (!/<img\b/i.test(match[0])) {
            insertPos = match.index + match[0].length;
            break;
          }
        }
        // Fallback to the first paragraph (legacy behavior) if no text paragraph
        // found. Guard against no `</p>` at all: `indexOf` returns -1, so leave
        // insertPos at -1 and prepend rather than splicing into byte offset 3.
        if (insertPos === -1) {
          const legacyEnd = htmlContent.indexOf("</p>");
          insertPos = legacyEnd === -1 ? -1 : legacyEnd + 4;
        }

        if (insertPos > 0) {
          htmlContent = htmlContent.slice(0, insertPos) + chainMessage + htmlContent.slice(insertPos);
        } else {
          // If no paragraph found, just prepend to the content
          htmlContent = chainMessage + htmlContent;
        }
      }
      htmlContent += "</div>";
    }

    const ccExtras = [
      ...filteredEnterpriseRepEmails.slice(0, MAX_REP_EMAILS),
      ...(event.cc_email_in_rfps ? [event.cc_email_in_rfps] : []),
      ...(handoffClientEmail ? [handoffClientEmail] : []),
    ].filter(isValidEmail);

    const payload = {
      event_id,
      venues: [venue],
      from,
      from_name,
      subject,
      htmlContent,
      chainReps,
      cc: ccExtras,
      attachments,
      allowTestEvent,
    };

    const response = await sendRfpTableEmail(payload);

    if (!response || response.status !== 200) {
      console.error("Error sending email");
      failed++;
    } else {
      updateTimestampRFPSent(supabase, venue.venue_static_id, event_id, source);
      sent++;

      // On a Northstar hand-off, flag this candidate so it's excluded from the
      // staff sourcing worklist, since the client — not Nowadays — now owns
      // follow-up with this venue. Done here (inside the per-venue loop) rather
      // than by the caller so every venue actually sent to gets flagged,
      // including chain-rep-fallback sends where the caller only sees an
      // aggregate result with no per-venue attribution.
      // The notes read is a snapshot from whenever the caller built `venues`
      // (e.g. when an admin panel was opened), not a fresh read — the same
      // accepted read-modify-write risk already documented on
      // `upsertRequestedCandidate` (requested-candidate.ts), now shared here too.
      if (handoffClientEmail) {
        const handoffFlagError = await flagHandoffCandidate(
          supabase,
          event_id,
          venue.venue_static_id,
          venue.candidate.internal_candidate_notes ?? null
        );
        if (handoffFlagError) {
          console.error("[sendRFPEmails] Failed to flag hand-off candidate", venue.venue_static_id, handoffFlagError);
        }
      }

      // Only flag as a "sent via chain rep" data-hygiene case once the send is
      // confirmed successful — a failed fallback send must not tell Josh it went out.
      if (!venue.email) {
        chainRepFallbackVenues.push(venue);
      }

      // Update the scraper email field in the candidate table
      const currentEmails = venue.candidate.candidate_contact_scraper_email
        ? venue.candidate.candidate_contact_scraper_email.split(", ").map((email) => email.toLowerCase())
        : [];
      const newEmails = venue.email ? [venue.email.toLowerCase()] : [];
      const updatedEmails = Array.from(new Set([...currentEmails, ...newEmails]));
      const fields = {
        event_venue_candidate_id: venue.candidate.event_venue_candidate_id,
        candidate_contact_scraper_email: updatedEmails.join(", "),
        candidate_contact_scraper_email_last_updated: Date.now(),
      };

      const success = await editHotelCandidate(supabase, fields);
      if (!success) {
        console.error("Failed to update candidate contact scraper email");
      }
    }

    // wait a bit before sending the next one (500 ms)
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  // Skip the "no contact on file" VA ping for the automatic customer-like auto-send
  // (source "liked-venue"): per product, a customer liking a no-email venue should no
  // longer surface "we have no email for this RFP" — neither to the customer (the toast
  // was reverted) nor to the VA here. That alert belongs to a deliberate send: the
  // enterprise dashboard has its own (notifyMissingVenueEmailSlack), and admin ops
  // ("admin-dash-rfp") / chain-rep sends still ping below.
  if (source !== LIKED_VENUE_SOURCE) {
    await notifyVaAboutVenueContact(supabase, noEmailVenues, event_id, "no-contact");
  }
  await notifyVaAboutVenueContact(supabase, chainRepFallbackVenues, event_id, "chain-rep-fallback");

  return {
    message: `Sent ${sent} emails/notifications, failed to send ${failed} emails/notifications.`,
    sent,
    failed,
    noEmailVenues,
  };
}

/**
 * Format meeting requirements from event agenda, the `num_meeting_rooms_needed`
 * scalar, and the legacy `meeting_rooms` free-text field. All three are combined
 * so none of the signals is dropped.
 * @param event - The event containing agenda and/or meeting-room data
 * @returns Formatted string with detailed agenda items plus any additional notes
 */
function formatMeetingRequirements(event: Events): string {
  // Scalar room need + legacy free-text note, combined.
  const notes = getMeetingRoomNotes(event);

  if (event.agenda && Array.isArray(event.agenda) && event.agenda.length > 0) {
    const agendaLines = sortAgendaChronologically(event.agenda).map((item) => {
      const dateStr =
        item.dates && item.dates.length > 0
          ? [...item.dates]
              .sort()
              .map((d) => moment(d).format("MMM D"))
              .join(", ")
          : "TBD";
      let line = `${item.title}: ${dateStr}`;
      if (item.startTime && item.endTime) line += ` (${item.startTime} - ${item.endTime})`;
      if (item.capacity) line += ` | ${item.capacity} pax`;
      if (item.roomType) line += ` | ${item.roomType}`;
      return line;
    });

    const meetingRoomsNote = notes.length > 0 ? `\n\nAdditional notes: ${notes.join("\n")}` : "";
    return agendaLines.join("\n") + meetingRoomsNote;
  }

  return notes.length > 0 ? notes.join("\n") : "N/A";
}

/**
 * Generate Hyatt Slack message details for one or more venues.
 * Used for posting RFP notifications to the #alexis-hyatt-nowadays channel.
 * @param event - The event the RFP is being sent for
 * @param hyattVenues - The Hyatt venues included in the RFP
 * @param eventOwner - Optional org owner used to populate organization/HQ fields
 * @returns Slack message title, summary, and structured detail rows
 */
export function generateHyattSlackMessageDetails(
  event: Events,
  // Only the identity/location fields are read here, so the param is widened to that structural
  // subset — letting the enterprise client-dashboard RFP flow (which resolves a base `StaticVenue`,
  // not a `StaticVenueWithMetadata`) reuse this builder without a lossy cast. Existing
  // `StaticVenueWithMetadata[]` callers still satisfy the narrower shape.
  hyattVenues: Pick<StaticVenueWithMetadata, "venue_name" | "venue_static_id" | "city" | "state" | "country">[],
  eventOwner?: { company?: string; headquarters_city?: string } | null
): {
  title: string;
  message: string;
  details: { title: string; message: string }[];
} {
  const dateRange = getEventDateRange(event);
  const startDate = dateRange ? moment(dateRange.startDate).format("MMMM D, YYYY") : "TBD";
  const endDate = dateRange ? moment(dateRange.endDate).format("MMMM D, YYYY") : "TBD";
  const preferred = getPreferredEventDates(event);
  const startTime = preferred?.startTime ? moment(preferred.startTime, "HH:mm:ss").format("hh:mm A") : "";
  const endTime = preferred?.endTime ? moment(preferred.endTime, "HH:mm:ss").format("hh:mm A") : "";

  const venueListText =
    hyattVenues.length > 1
      ? "\n\nVenues:\n" + hyattVenues.map((venue) => `• ${venue.venue_name} (${venue.venue_static_id})`).join("\n")
      : "";

  const eventDetails: { title: string; message: string }[] = [
    {
      title: "Event Name",
      message: event.name + ` (<https://nowadays.ai/admin?event=${event.event_id}|ev-${event.event_id}>)`,
    },
    {
      title: "Event Date(s)",
      message:
        startDate === endDate
          ? startTime && endTime
            ? `${startDate} from ${startTime} to ${endTime}`
            : startDate
          : `Check-in: ${startDate}\nCheck-out: ${endDate}`,
    },
    { title: "Flexibility", message: event.flexibility ?? "Dates/Times Not Flexible" },
    { title: "City of Consideration", message: event.location },
    { title: "Organization", message: eventOwner?.company || "N/A" },
    { title: "HQ City", message: eventOwner?.headquarters_city || "N/A" },
    { title: "Room Requirements", message: formatAccommodationsForSlack(event.accommodations) },
    {
      title: "Attendees",
      message: event.num_people && event.num_people > 0 ? `${event.num_people} people` : "N/A",
    },
    { title: "Meeting Requirements", message: formatMeetingRequirements(event) },
    { title: "F&B Requirements", message: event.food_beverage_requirements || "N/A" },
    { title: "Other Notes", message: event.other_notes || "N/A" },
    { title: "Origin", message: event.origin || "N/A" },
    {
      title: "Signatory",
      message: "The contract will be signed by the client company, not our agency",
    },
    { title: "POC", message: "Based in Mountain View, CA (94040)" },
    {
      title: "Agency Details",
      message: `Nowadays\nIATA #23742950\n2712 61st Avenue SE, Mercer Island, WA 98040\n${
        event.is_northstar_event ? "+1 (650) 880-6497" : "(650) 379-0123"
      }`,
    },
  ];

  const venueDetails = hyattVenues
    .map((venue) => [
      { title: "---", message: "---" },
      { title: "Venue Name", message: venue.venue_name },
      {
        title: "Venue Static ID (internal Nowadays use only)",
        message: venue.venue_static_id.toString(),
      },
      // formatCityState, not a raw join: a state-less venue rendered "Cancun, null"
      // into the Slack card (ENG-550).
      { title: "Venue Location", message: formatCityState(venue.city, venue.state, venue.country) },
    ])
    .flat();

  return {
    title: `<@${ALEXIS_SLACK_USER_ID}> New Hyatt RFP: ${hyattVenues.length} venue${
      hyattVenues.length > 1 ? "s" : ""
    } for ${event.name} (<https://nowadays.ai/admin?event=${event.event_id}|ev-${event.event_id}>)${venueListText}`,
    message: `RFP Details for ${hyattVenues.length} Hyatt venue${hyattVenues.length > 1 ? "s" : ""}`,
    details: [...venueDetails, { title: "---", message: "---" }, ...eventDetails],
  };
}

export function constructEventDetails(
  event: Events,
  org_industry: string | null,
  org_headquarters_city: string | null
) {
  let draft = `\nEvent Name: ev-${event.event_id} ${event.name}\n`;

  draft += `Client Industry: ${org_industry || "{TODO}"}\nClient Headquarters City: ${org_headquarters_city || "{TODO}"}`;

  const dateRange = getEventDateRange(event);
  const startDate = dateRange ? moment(dateRange.startDate).format("MMMM D, YYYY") : "TBD";
  const endDate = dateRange ? moment(dateRange.endDate).format("MMMM D, YYYY") : "TBD";
  const decisionDeadline = moment(event.decision_deadline).format("MMMM D, YYYY");

  const preferred = getPreferredEventDates(event);
  const startTime = preferred?.startTime ? moment(preferred.startTime, "HH:mm:ss").format("hh:mm A") : "";
  const endTime = preferred?.endTime ? moment(preferred.endTime, "HH:mm:ss").format("hh:mm A") : "";

  if (startDate === endDate) {
    if (startTime && endTime) {
      draft += `\nDate: ${startDate} from ${startTime} to ${endTime}`;
    } else {
      draft += `\nDate: ${startDate}`;
    }
  } else {
    draft += `\nCheck-in: ${startDate}\nCheck-out: ${endDate}`;
  }

  draft += `\nFlexibility: ${event.flexibility ?? "Dates/Times Not Flexible"}`;

  draft += `\nCity of Consideration: ${event.location}`;

  // Add detailed accommodations breakdown. `accommodations` is stored in two shapes
  // (array vs legacy date-keyed object); normalize so object-shaped events still get
  // their room block in the draft instead of being silently skipped.
  const accommodationsRows = normalizeAccommodationsToArray(event.accommodations);
  if (accommodationsRows.length > 0) {
    draft += `\n# Accommodations:`;

    // Calculate peak for summary
    const peakRooms = getPeakRoomCount(accommodationsRows);
    draft += `\n  Peak requirement: ${peakRooms} rooms`;

    // Add detailed breakdown by date. `normalizeAccommodationsToArray` already returns
    // entries sorted chronologically by their `YYYY-MM-DD` date, so no re-sort is needed.
    draft += `\n  Breakdown by date:`;
    accommodationsRows.forEach((dayAccommodation) => {
      const roomTypes = Object.entries(dayAccommodation)
        .filter(([key, count]) => key !== "date" && typeof count === "number" && count > 0)
        .map(([type, count]) => `${count} ${type}`)
        .join(", ");

      if (roomTypes) {
        // Validate date before formatting
        const date = moment(dayAccommodation.date);
        const formattedDate = date.isValid() ? date.format("MMMM D, YYYY") : "Date TBD";
        draft += `\n    ${formattedDate}: ${roomTypes}`;
      }
    });
  }
  if (event.num_people > 0) {
    draft += `\n# Attendees: ${event.num_people} people`;
  }
  if (event.meeting_rooms) {
    draft += `\nMeeting requirements: ${event.meeting_rooms}`;
  }

  if (event.food_beverage_requirements) {
    draft += `\nF&B requirements: ${event.food_beverage_requirements}`;
  }

  draft += `\nContact me at: ina+${event.event_id}@nowadays.ai`;

  if (event.other_notes) {
    draft += `\nOther: ${event.other_notes}`;
  }

  // Add new fields
  if (event.notes_location_budget) {
    draft += `\nLocation & Budget Notes: ${event.notes_location_budget}`;
  }

  if (event.event_dates && Array.isArray(event.event_dates) && event.event_dates.length > 0) {
    draft += `\nEvent Date Options:`;
    event.event_dates.forEach((dateOption: any, index: number) => {
      // Handle both single-day and multi-day events
      if (dateOption.date) {
        // Single-day event
        const date = moment(dateOption.date).format("MMM D, YYYY");
        draft += `\n  Option ${index + 1}: ${date}`;
        if (dateOption.startTime && dateOption.endTime) {
          draft += ` (${dateOption.startTime} - ${dateOption.endTime})`;
        }
      } else if (dateOption.startDate && dateOption.endDate) {
        // Multi-day event
        const start = moment(dateOption.startDate).format("MMM D, YYYY");
        const end = moment(dateOption.endDate).format("MMM D, YYYY");
        draft += `\n  Option ${index + 1}: ${start} to ${end}`;
      }
    });
  }

  if (event.agenda && Array.isArray(event.agenda) && event.agenda.length > 0) {
    draft += `\nAgenda:`;
    sortAgendaChronologically(event.agenda).forEach((item: any) => {
      // Handle dates array or single date
      const dateStr =
        item.dates && item.dates.length > 0
          ? [...item.dates]
              .sort()
              .map((d: string) => moment(d).format("MMM D"))
              .join(", ")
          : "TBD";
      draft += `\n  ${dateStr} - ${item.title}`;
      if (item.startTime && item.endTime) {
        draft += ` (${item.startTime} - ${item.endTime})`;
      }
      if (item.capacity) draft += ` [Capacity: ${item.capacity}]`;
      if (item.roomType) draft += ` [Room: ${item.roomType}]`;
      if (item.avRequirements && item.avRequirements.length > 0) {
        draft += `\n    A/V: ${item.avRequirements.join(", ")}`;
        if (item.avOtherText) {
          draft += `, ${item.avOtherText}`;
        }
      }
      if (item.fbRequirements && item.fbRequirements.length > 0) {
        draft += `\n    F&B: ${item.fbRequirements.join(", ")}`;
        if (item.fbOtherText) {
          draft += `, ${item.fbOtherText}`;
        }
      }
      if (item.notes) draft += `\n    Notes: ${item.notes}`;
    });
  }

  if (event.decision_factors && Array.isArray(event.decision_factors) && event.decision_factors.length > 0) {
    draft += `\nDecision Factors: ${event.decision_factors.join(", ")}`;
    if (event.decision_factors.includes("Other") && event.decision_factor_other_text) {
      draft += ` - ${event.decision_factor_other_text}`;
    }
  }

  if (event.custom_questions && Array.isArray(event.custom_questions) && event.custom_questions.length > 0) {
    draft += `\nCustom Questions: ${event.custom_questions.join(", ")}`;
  }
  if (event.custom_concessions && Array.isArray(event.custom_concessions) && event.custom_concessions.length > 0) {
    draft += `\nCustom Concessions: ${event.custom_concessions.join(", ")}`;
  }

  draft += `\nSignatory: The contract will be signed by the client company, not our agency.`;

  draft += `\nPOC: I'll be the main contact until the contract is signed! I'm based in Mountain View, CA and my zip code is 94040. Please email me at ina+${event.event_id}@nowadays.ai`;

  draft += `\nNotes: We're an IATA-certified travel agency. Our agency name is Nowadays, and our address is 2712 61st Avenue SE, Mercer Island, WA 98040. Phone number is ${event.is_northstar_event ? "+1 (650) 880-6497" : "(650) 379-0123"}. Our IATA number is 23742950.`;

  return draft;
}

/**
 * @param chainRepLookupClient - Service-role client for resolving chain reps when
 *   `supabase` is a customer's authenticated session — see `sendRFPEmails` for why.
 *   Server-side callers acting on behalf of a customer should pass one.
 */
export async function sendRFPEmailsAutomatically(
  supabase: SupabaseClient,
  event: Events,
  eventOwner: Users | null,
  venues: VenueOption[],
  chainRepLookupClient?: SupabaseClient
): Promise<{
  subject: string;
  email: string;
  results: {
    successfulVenues: string[];
    failedVenues: string[];
    skippedVenues: string[];
  };
} | null> {
  console.log("[SEND-RFP-AUTO] Starting for event", event.event_id, "with", venues.length, "venues");

  if (isTestEvent(event)) {
    console.log(`[SEND-RFP-AUTO] Skipping for test event ${event.event_id} (${event.name})`);
    return null;
  }
  // Track skipped venues (venues with active quotes)
  const skippedVenues: string[] = venues
    .filter((option) => option.quotes.some((q) => q.is_active && q.grand_total != null && q.grand_total !== 0))
    .map((option) => option.venue.venue_name);

  // Filter out venues that already have active quotes
  const venuesToProcess = venues.filter(
    (option) => !option.quotes.some((q) => q.is_active && q.grand_total != null && q.grand_total !== 0)
  );

  console.log("[SEND-RFP-AUTO] Skipped venues with quotes:", skippedVenues.length);
  console.log("[SEND-RFP-AUTO] Venues to process (no quotes):", venuesToProcess.length);

  // Snapshot-gated draft resolution (ENG-150): reuses the stored draft while
  // its disclosure snapshot is current, regenerates (CAS-serialized, service
  // role) when disclosure changed. Server action so browser callers (event
  // creation, un-awaited venue-like) never resolve disclosure with their own role.
  const draftResult = await resolveCurrentRfpDraftAction(event.event_id, "rfp");
  if (!draftResult.success) {
    console.error("[SEND-RFP-AUTO] Failed to resolve RFP draft for event", event.event_id, draftResult.error);
    // The old path threw here (noisy); a silent null would strand the event
    // with zero RFPs sent and nobody told — keep engineering in the loop.
    await notifyRfpAutoDraftFailed({
      eventId: event.event_id,
      skippedVenueCount: venues.length,
      reason: draftResult.error,
    }).catch((notifyError) => console.error("[SEND-RFP-AUTO] Failed to post draft-failure alert:", notifyError));
    return null;
  }
  const draft = draftResult.data;
  const { subject, body: email } = draft;

  if (draft.regenerated) {
    console.log("[SEND-RFP-AUTO] Regenerated RFP email body");
    // send slack message of the client questions
    await sendSlackMessage(
      SlackNotificationType.TEST,
      `[AUTO] RFP Email Drafted to send`,
      `Drafted RFP email to send to ${venuesToProcess.length} venues. ${skippedVenues.length > 0 ? `Skipped ${skippedVenues.length} venues with existing quotes.` : ""} HTML converted to plain text so it's easier to read over Slack.`,
      [
        { title: "Event ID", message: event.event_id.toString() },
        { title: "Subject", message: subject },
        { title: "Email", message: convertHtmlToPlainText(email) },
        { title: "Client Questions", message: draft.clientQuestions?.join("\n") || "N/A" },
        { title: "Skipped Venues (Has Quote)", message: skippedVenues.length > 0 ? skippedVenues.join(", ") : "None" },
      ]
    );
  } else {
    console.log("[SEND-RFP-AUTO] Using existing RFP email body from event");
  }

  // only send to venues that haven't already been sent to already (check both timestamp_rfp_sent and timestamp_rfp_sent_failed)
  const venuesNoRFPSentTo = venues.filter(
    (venue) => !venue.candidate.timestamp_rfp_sent && !venue.candidate.timestamp_rfp_sent_failed
  );

  console.log(
    "[SEND-RFP-AUTO] Venues without timestamp_rfp_sent or timestamp_rfp_sent_failed:",
    venuesNoRFPSentTo.length
  );
  console.log(
    "[SEND-RFP-AUTO] Venue details:",
    venuesNoRFPSentTo.map((v) => ({
      name: v.venue.venue_name,
      email: v.venue.email,
      has_email: !!v.venue.email,
    }))
  );

  const successfulVenues: string[] = [];
  const failedVenues: string[] = [];
  const failedVenueReasons: Record<string, string> = {}; // Track failure reasons
  const successfulHyattVenues: StaticVenueWithMetadata[] = [];

  // Process venues in batches of 5 concurrently
  if (venuesNoRFPSentTo.length > 0) {
    const BATCH_SIZE = 5;
    const venueChunks = chunkArray(venuesNoRFPSentTo, BATCH_SIZE);

    for (const chunk of venueChunks) {
      // Process each chunk in parallel
      const chunkPromises = chunk.map((venue) =>
        sendRFPEmails(
          supabase,
          subject,
          email,
          [{ ...venue.venue, candidate: venue.candidate, quotes: undefined }],
          "ina@nowadays.ai",
          event,
          LIKED_VENUE_SOURCE,
          undefined,
          undefined,
          undefined,
          undefined,
          chainRepLookupClient
        )
          .then(async (result) => {
            if (result) {
              // Check if venue has no email
              const hasNoEmail = result.noEmailVenues.some((v) => v.venue_static_id === venue.venue.venue_static_id);

              if (hasNoEmail) {
                // Update timestamp_rfp_sent_failed for venues without emails
                try {
                  await updateTimestampRFPSentFailed(supabase, venue.venue.venue_static_id, event.event_id);
                  console.log(`[SEND-RFP-AUTO] Set timestamp_rfp_sent_failed for ${venue.venue.venue_name} (no email)`);
                } catch (error) {
                  console.error(
                    `[SEND-RFP-AUTO] Error updating timestamp_rfp_sent_failed for ${venue.venue.venue_name}:`,
                    error
                  );
                }
                failedVenues.push(venue.venue.venue_name);
                failedVenueReasons[venue.venue.venue_name] = "No email address";
              } else if (result.sent > 0) {
                successfulVenues.push(venue.venue.venue_name);
                if (venue.venue.chain_name === HYATT_CHAIN_NAME) {
                  successfulHyattVenues.push({ ...venue.venue, candidate: venue.candidate, quotes: undefined });
                }
              } else {
                failedVenues.push(venue.venue.venue_name);
                failedVenueReasons[venue.venue.venue_name] = result.failed > 0 ? "Email send failed" : "Unknown error";
              }
            } else {
              failedVenues.push(venue.venue.venue_name);
              failedVenueReasons[venue.venue.venue_name] = "Unknown error";
            }
          })
          .catch((error) => {
            console.error(`Error sending email to ${venue.venue.venue_name}:`, error);
            failedVenues.push(venue.venue.venue_name);
            failedVenueReasons[venue.venue.venue_name] = error.message || "Exception occurred";
          })
      );

      // Wait for current batch to complete before processing next batch
      await Promise.all(chunkPromises);

      // Add a small delay between batches to prevent overwhelming the server
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  // Notify #alexis-hyatt-nowadays for Hyatt venues whose RFP email actually succeeded.
  // Mirrors the post-success gating used by the manual admin-dash flow.
  if (successfulHyattVenues.length > 0) {
    try {
      const slackDetails = generateHyattSlackMessageDetails(event, successfulHyattVenues, eventOwner);
      await sendSlackMessage(
        SlackNotificationType.HYATT,
        slackDetails.title,
        slackDetails.message,
        slackDetails.details
      );
    } catch (error) {
      console.error("[SEND-RFP-AUTO] Error sending Alexis Hyatt Slack notification:", error);
      // Auto-send has no UI surface — escalate to admin channel so ops sees the silent failure
      // and can post to #alexis-hyatt-nowadays manually.
      try {
        await notifyHyattSlackFailed({
          eventId: event.event_id,
          venues: successfulHyattVenues.map((v) => ({ venueName: v.venue_name, venueStaticId: v.venue_static_id })),
        });
      } catch (adminError) {
        console.error("[SEND-RFP-AUTO] Admin fallback notification also failed:", adminError);
      }
    }
  }

  if (venuesNoRFPSentTo.length > 0 && failedVenues.length > 0) {
    await notifyRfpFailedVenues({
      eventId: event.event_id,
      subject,
      failedVenues: failedVenues.map((venueName) => {
        const venue = venuesNoRFPSentTo.find((v) => v.venue.venue_name === venueName);
        return {
          venueName,
          venueStaticId: venue ? venue.venue.venue_static_id : null,
          reason: failedVenueReasons[venueName] || "Unknown reason",
        };
      }),
      skippedVenues,
    }).catch((notifyError) => console.error("[SEND-RFP-AUTO] Failed to post failed-venues alert:", notifyError));
  }

  return {
    subject,
    email,
    results: {
      successfulVenues,
      failedVenues,
      skippedVenues,
    },
  };
}

// only the admin can actually successfully run this function
export async function sendCustomerEmail(
  supabase: SupabaseClient,
  subject: string,
  draft: string,
  to: string,
  from: string,
  event_id?: number,
  ignoreSlack?: boolean,
  from_name?: string,
  reply_to?: string,
  attachments?: EmailAttachment[],
  cc?: string,
  bcc?: string
): Promise<boolean> {
  try {
    const response = await fetchPOSTNext("/api/email/send_email", {
      subject,
      draft,
      to,
      from,
      event_id,
      from_name,
      reply_to,
      attachments,
      cc,
      bcc,
    });

    if (!response || response.status !== 200) {
      console.error("Error sending email");
      return false;
    }

    if (!ignoreSlack) {
      notifyCustomerEmailSent({ from, to, subject, eventId: event_id }).catch(console.error);
    }

    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export enum RFPEmailStage {
  EVENT_CREATED = "event_created",
  VENUES_LIKED = "venues_liked",
  RFP_SENT = "rfp_sent",
}

export interface RFPEmailTemplateParams {
  event: Events;
  user: Users;
  isNorthstar: boolean;
  stage: RFPEmailStage;
  likedVenues?: VenueOption[];
  requestedVenue?: string;
}

// Helper function to get the appropriate API URL based on context
const getApiUrl = (path: string): string => {
  // Check if we're on the server side
  if (typeof window === "undefined") {
    // Server side - use absolute URL
    const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";
    return `${baseUrl}${path}`;
  }
  // Client side - use relative URL to avoid CORS
  return path;
};

export async function generateEmailConfirmation({
  event,
  user,
  isNorthstar,
  stage,
  likedVenues = [],
  requestedVenue,
}: RFPEmailTemplateParams): Promise<void> {
  try {
    const response = await fetch(getApiUrl("/api/emails/generate-confirmation-email"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        event,
        user,
        isNorthstar,
        stage,
        likedVenues,
        requestedVenue,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to send email confirmation");
    }
  } catch (error) {
    console.error("Error sending email confirmation:", error);
    throw error;
  }
}

export const currencyToCountry = new Map<string, string>([
  ["AFN", "AFGHANISTAN"],
  ["EUR", "ÅLAND ISLANDS"],
  ["ALL", "ALBANIA"],
  ["DZD", "ALGERIA"],
  ["USD", "AMERICAN SAMOA"],
  ["EUR", "ANDORRA"],
  ["AOA", "ANGOLA"],
  ["XCD", "ANGUILLA"],
  ["XCD", "ANTIGUA AND BARBUDA"],
  ["ARS", "ARGENTINA"],
  ["AMD", "ARMENIA"],
  ["AWG", "ARUBA"],
  ["AUD", "AUSTRALIA"],
  ["EUR", "AUSTRIA"],
  ["AZN", "AZERBAIJAN"],
  ["BSD", "BAHAMAS (THE)"],
  ["BHD", "BAHRAIN"],
  ["BDT", "BANGLADESH"],
  ["BBD", "BARBADOS"],
  ["BYN", "BELARUS"],
  ["EUR", "BELGIUM"],
  ["BZD", "BELIZE"],
  ["XOF", "BENIN"],
  ["BMD", "BERMUDA"],
  ["BTN", "BHUTAN"],
  ["BOV", "BOLIVIA (PLURINATIONAL STATE OF)"],
  ["USD", "BONAIRE, SINT EUSTATIUS AND SABA"],
  ["BAM", "BOSNIA AND HERZEGOVINA"],
  ["BWP", "BOTSWANA"],
  ["NOK", "BOUVET ISLAND"],
  ["BRL", "BRAZIL"],
  ["USD", "BRITISH INDIAN OCEAN TERRITORY (THE)"],
  ["BND", "BRUNEI DARUSSALAM"],
  ["BGN", "BULGARIA"],
  ["XOF", "BURKINA FASO"],
  ["BIF", "BURUNDI"],
  ["CVE", "CABO VERDE"],
  ["KHR", "CAMBODIA"],
  ["XAF", "CAMEROON"],
  ["CAD", "CANADA"],
  ["KYD", "CAYMAN ISLANDS (THE)"],
  ["XAF", "CENTRAL AFRICAN REPUBLIC (THE)"],
  ["XAF", "CHAD"],
  ["CLF", "CHILE"],
  ["CNY", "CHINA"],
  ["AUD", "CHRISTMAS ISLAND"],
  ["AUD", "COCOS (KEELING) ISLANDS (THE)"],
  ["COU", "COLOMBIA"],
  ["KMF", "COMOROS (THE)"],
  ["CDF", "CONGO (THE DEMOCRATIC REPUBLIC OF THE)"],
  ["XAF", "CONGO (THE)"],
  ["NZD", "COOK ISLANDS (THE)"],
  ["CRC", "COSTA RICA"],
  ["XOF", "CÔTE D'IVOIRE"],
  ["EUR", "CROATIA"],
  ["CUC", "CUBA"],
  ["ANG", "CURAÇAO"],
  ["EUR", "CYPRUS"],
  ["CZK", "CZECHIA"],
  ["DKK", "DENMARK"],
  ["DJF", "DJIBOUTI"],
  ["XCD", "DOMINICA"],
  ["DOP", "DOMINICAN REPUBLIC (THE)"],
  ["USD", "ECUADOR"],
  ["EGP", "EGYPT"],
  ["USD", "EL SALVADOR"],
  ["XAF", "EQUATORIAL GUINEA"],
  ["ERN", "ERITREA"],
  ["EUR", "ESTONIA"],
  ["SZL", "ESWATINI"],
  ["ETB", "ETHIOPIA"],
  ["EUR", "EUROPEAN UNION"],
  ["FKP", "FALKLAND ISLANDS (THE) [MALVINAS]"],
  ["DKK", "FAROE ISLANDS (THE)"],
  ["FJD", "FIJI"],
  ["EUR", "FINLAND"],
  ["EUR", "FRANCE"],
  ["EUR", "FRENCH GUIANA"],
  ["XPF", "FRENCH POLYNESIA"],
  ["EUR", "FRENCH SOUTHERN TERRITORIES (THE)"],
  ["XAF", "GABON"],
  ["GMD", "GAMBIA (THE)"],
  ["GEL", "GEORGIA"],
  ["EUR", "GERMANY"],
  ["GHS", "GHANA"],
  ["GIP", "GIBRALTAR"],
  ["EUR", "GREECE"],
  ["DKK", "GREENLAND"],
  ["XCD", "GRENADA"],
  ["EUR", "GUADELOUPE"],
  ["USD", "GUAM"],
  ["GTQ", "GUATEMALA"],
  ["GBP", "GUERNSEY"],
  ["GNF", "GUINEA"],
  ["XOF", "GUINEA-BISSAU"],
  ["GYD", "GUYANA"],
  ["USD", "HAITI"],
  ["AUD", "HEARD ISLAND AND McDONALD ISLANDS"],
  ["EUR", "HOLY SEE (THE)"],
  ["HNL", "HONDURAS"],
  ["HKD", "HONG KONG"],
  ["HUF", "HUNGARY"],
  ["ISK", "ICELAND"],
  ["INR", "INDIA"],
  ["IDR", "INDONESIA"],
  ["XDR", "INTERNATIONAL MONETARY FUND (IMF) "],
  ["IRR", "IRAN (ISLAMIC REPUBLIC OF)"],
  ["IQD", "IRAQ"],
  ["EUR", "IRELAND"],
  ["GBP", "ISLE OF MAN"],
  ["ILS", "ISRAEL"],
  ["EUR", "ITALY"],
  ["JMD", "JAMAICA"],
  ["JPY", "JAPAN"],
  ["GBP", "JERSEY"],
  ["JOD", "JORDAN"],
  ["KZT", "KAZAKHSTAN"],
  ["KES", "KENYA"],
  ["AUD", "KIRIBATI"],
  ["KPW", "KOREA (THE DEMOCRATIC PEOPLE'S REPUBLIC OF)"],
  ["KRW", "KOREA (THE REPUBLIC OF)"],
  ["KWD", "KUWAIT"],
  ["KGS", "KYRGYZSTAN"],
  ["LAK", "LAO PEOPLE'S DEMOCRATIC REPUBLIC (THE)"],
  ["EUR", "LATVIA"],
  ["LBP", "LEBANON"],
  ["ZAR", "LESOTHO"],
  ["LRD", "LIBERIA"],
  ["LYD", "LIBYA"],
  ["CHF", "LIECHTENSTEIN"],
  ["EUR", "LITHUANIA"],
  ["EUR", "LUXEMBOURG"],
  ["MOP", "MACAO"],
  ["MKD", "NORTH MACEDONIA"],
  ["MGA", "MADAGASCAR"],
  ["MWK", "MALAWI"],
  ["MYR", "MALAYSIA"],
  ["MVR", "MALDIVES"],
  ["XOF", "MALI"],
  ["EUR", "MALTA"],
  ["USD", "MARSHALL ISLANDS (THE)"],
  ["EUR", "MARTINIQUE"],
  ["MRU", "MAURITANIA"],
  ["MUR", "MAURITIUS"],
  ["EUR", "MAYOTTE"],
  ["XUA", "MEMBER COUNTRIES OF THE AFRICAN DEVELOPMENT BANK GROUP"],
  ["MXV", "MEXICO"],
  ["MXN", "MEXICO"],
  ["USD", "MICRONESIA (FEDERATED STATES OF)"],
  ["MDL", "MOLDOVA (THE REPUBLIC OF)"],
  ["EUR", "MONACO"],
  ["MNT", "MONGOLIA"],
  ["EUR", "MONTENEGRO"],
  ["XCD", "MONTSERRAT"],
  ["MAD", "MOROCCO"],
  ["MZN", "MOZAMBIQUE"],
  ["MMK", "MYANMAR"],
  ["ZAR", "NAMIBIA"],
  ["AUD", "NAURU"],
  ["NPR", "NEPAL"],
  ["EUR", "NETHERLANDS (THE)"],
  ["XPF", "NEW CALEDONIA"],
  ["NZD", "NEW ZEALAND"],
  ["NIO", "NICARAGUA"],
  ["XOF", "NIGER (THE)"],
  ["NGN", "NIGERIA"],
  ["NZD", "NIUE"],
  ["AUD", "NORFOLK ISLAND"],
  ["USD", "NORTHERN MARIANA ISLANDS (THE)"],
  ["NOK", "NORWAY"],
  ["OMR", "OMAN"],
  ["PKR", "PAKISTAN"],
  ["USD", "PALAU"],
  ["USD", "PANAMA"],
  ["PGK", "PAPUA NEW GUINEA"],
  ["PYG", "PARAGUAY"],
  ["PEN", "PERU"],
  ["PHP", "PHILIPPINES (THE)"],
  ["NZD", "PITCAIRN"],
  ["PLN", "POLAND"],
  ["EUR", "PORTUGAL"],
  ["USD", "PUERTO RICO"],
  ["QAR", "QATAR"],
  ["EUR", "RÉUNION"],
  ["RON", "ROMANIA"],
  ["RUB", "RUSSIAN FEDERATION (THE)"],
  ["RWF", "RWANDA"],
  ["EUR", "SAINT BARTHÉLEMY"],
  ["SHP", "SAINT HELENA, ASCENSION AND TRISTAN DA CUNHA"],
  ["XCD", "SAINT KITTS AND NEVIS"],
  ["XCD", "SAINT LUCIA"],
  ["EUR", "SAINT MARTIN (FRENCH PART)"],
  ["EUR", "SAINT PIERRE AND MIQUELON"],
  ["XCD", "SAINT VINCENT AND THE GRENADINES"],
  ["WST", "SAMOA"],
  ["EUR", "SAN MARINO"],
  ["STN", "SAO TOME AND PRINCIPE"],
  ["SAR", "SAUDI ARABIA"],
  ["XOF", "SENEGAL"],
  ["RSD", "SERBIA"],
  ["SCR", "SEYCHELLES"],
  ["SLE", "SIERRA LEONE"],
  ["SGD", "SINGAPORE"],
  ["ANG", "SINT MAARTEN (DUTCH PART)"],
  ["XSU", 'SISTEMA UNITARIO DE COMPENSACION REGIONAL DE PAGOS "SUCRE"'],
  ["EUR", "SLOVAKIA"],
  ["EUR", "SLOVENIA"],
  ["SBD", "SOLOMON ISLANDS"],
  ["SOS", "SOMALIA"],
  ["ZAR", "SOUTH AFRICA"],
  ["SSP", "SOUTH SUDAN"],
  ["EUR", "SPAIN"],
  ["LKR", "SRI LANKA"],
  ["SDG", "SUDAN (THE)"],
  ["SRD", "SURINAME"],
  ["NOK", "SVALBARD AND JAN MAYEN"],
  ["SEK", "SWEDEN"],
  ["CHW", "SWITZERLAND"],
  ["SYP", "SYRIAN ARAB REPUBLIC"],
  ["TWD", "TAIWAN (PROVINCE OF CHINA)"],
  ["TJS", "TAJIKISTAN"],
  ["TZS", "TANZANIA, UNITED REPUBLIC OF"],
  ["THB", "THAILAND"],
  ["USD", "TIMOR-LESTE"],
  ["XOF", "TOGO"],
  ["NZD", "TOKELAU"],
  ["TOP", "TONGA"],
  ["TTD", "TRINIDAD AND TOBAGO"],
  ["TND", "TUNISIA"],
  ["TRY", "TÜRKİYE"],
  ["TMT", "TURKMENISTAN"],
  ["USD", "TURKS AND CAICOS ISLANDS (THE)"],
  ["AUD", "TUVALU"],
  ["UGX", "UGANDA"],
  ["UAH", "UKRAINE"],
  ["AED", "UNITED ARAB EMIRATES (THE)"],
  ["GBP", "UNITED KINGDOM OF GREAT BRITAIN AND NORTHERN IRELAND (THE)"],
  ["USD", "UNITED STATES MINOR OUTLYING ISLANDS (THE)"],
  ["USN", "UNITED STATES OF AMERICA (THE)"],
  ["UYW", "URUGUAY"],
  ["UZS", "UZBEKISTAN"],
  ["VUV", "VANUATU"],
  ["VED", "VENEZUELA (BOLIVARIAN REPUBLIC OF)"],
  ["VND", "VIET NAM"],
  ["USD", "VIRGIN ISLANDS (BRITISH)"],
  ["USD", "VIRGIN ISLANDS (U.S.)"],
  ["XPF", "WALLIS AND FUTUNA"],
  ["MAD", "WESTERN SAHARA"],
  ["YER", "YEMEN"],
  ["ZMW", "ZAMBIA"],
  ["ZWL", "ZIMBABWE"],
  ["XBA", "ZZ01_Bond Markets Unit European_EURCO"],
  ["XBB", "ZZ02_Bond Markets Unit European_EMU-6"],
  ["XBC", "ZZ03_Bond Markets Unit European_EUA-9"],
  ["XBD", "ZZ04_Bond Markets Unit European_EUA-17"],
  ["XTS", "ZZ06_Testing_Code"],
  ["XXX", "ZZ07_No_Currency"],
  ["XAU", "ZZ08_Gold"],
  ["XPD", "ZZ09_Palladium"],
  ["XPT", "ZZ10_Platinum"],
  ["XAG", "ZZ11_Silver"],
]);

export const validCurrencies = new Set<string>([
  "AFN",
  "EUR",
  "ALL",
  "DZD",
  "USD",
  "EUR",
  "AOA",
  "XCD",
  "XCD",
  "ARS",
  "AMD",
  "AWG",
  "AUD",
  "EUR",
  "AZN",
  "BSD",
  "BHD",
  "BDT",
  "BBD",
  "BYN",
  "EUR",
  "BZD",
  "XOF",
  "BMD",
  "BTN",
  "BOV",
  "USD",
  "BAM",
  "BWP",
  "NOK",
  "BRL",
  "USD",
  "BND",
  "BGN",
  "XOF",
  "BIF",
  "CVE",
  "KHR",
  "XAF",
  "CAD",
  "KYD",
  "XAF",
  "XAF",
  "CLF",
  "CNY",
  "AUD",
  "AUD",
  "COU",
  "KMF",
  "CDF",
  "XAF",
  "NZD",
  "CRC",
  "XOF",
  "EUR",
  "CUC",
  "ANG",
  "EUR",
  "CZK",
  "DKK",
  "DJF",
  "XCD",
  "DOP",
  "USD",
  "EGP",
  "USD",
  "XAF",
  "ERN",
  "EUR",
  "SZL",
  "ETB",
  "EUR",
  "FKP",
  "DKK",
  "FJD",
  "EUR",
  "EUR",
  "EUR",
  "XPF",
  "EUR",
  "XAF",
  "GMD",
  "GEL",
  "EUR",
  "GHS",
  "GIP",
  "EUR",
  "DKK",
  "XCD",
  "EUR",
  "USD",
  "GTQ",
  "GBP",
  "GNF",
  "XOF",
  "GYD",
  "USD",
  "AUD",
  "EUR",
  "HNL",
  "HKD",
  "HUF",
  "ISK",
  "INR",
  "IDR",
  "XDR",
  "IRR",
  "IQD",
  "EUR",
  "GBP",
  "ILS",
  "EUR",
  "JMD",
  "JPY",
  "GBP",
  "JOD",
  "KZT",
  "KES",
  "AUD",
  "KPW",
  "KRW",
  "KWD",
  "KGS",
  "LAK",
  "EUR",
  "LBP",
  "ZAR",
  "LRD",
  "LYD",
  "CHF",
  "EUR",
  "EUR",
  "MOP",
  "MKD",
  "MGA",
  "MWK",
  "MYR",
  "MVR",
  "XOF",
  "EUR",
  "USD",
  "EUR",
  "MRU",
  "MUR",
  "EUR",
  "XUA",
  "MXV",
  "MXN",
  "USD",
  "MDL",
  "EUR",
  "MNT",
  "EUR",
  "XCD",
  "MAD",
  "MZN",
  "MMK",
  "ZAR",
  "AUD",
  "NPR",
  "EUR",
  "XPF",
  "NZD",
  "NIO",
  "XOF",
  "NGN",
  "NZD",
  "AUD",
  "USD",
  "NOK",
  "OMR",
  "PKR",
  "USD",
  "USD",
  "PGK",
  "PYG",
  "PEN",
  "PHP",
  "NZD",
  "PLN",
  "EUR",
  "USD",
  "QAR",
  "EUR",
  "RON",
  "RUB",
  "RWF",
  "EUR",
  "SHP",
  "XCD",
  "XCD",
  "EUR",
  "EUR",
  "XCD",
  "WST",
  "EUR",
  "STN",
  "SAR",
  "XOF",
  "RSD",
  "SCR",
  "SLE",
  "SGD",
  "ANG",
  "XSU",
  "EUR",
  "EUR",
  "SBD",
  "SOS",
  "ZAR",
  "SSP",
  "EUR",
  "LKR",
  "SDG",
  "SRD",
  "NOK",
  "SEK",
  "CHW",
  "SYP",
  "TWD",
  "TJS",
  "TZS",
  "THB",
  "USD",
  "XOF",
  "NZD",
  "TOP",
  "TTD",
  "TND",
  "TRY",
  "TMT",
  "USD",
  "AUD",
  "UGX",
  "UAH",
  "AED",
  "GBP",
  "USD",
  "USN",
  "UYW",
  "UZS",
  "VUV",
  "VED",
  "VND",
  "USD",
  "USD",
  "XPF",
  "MAD",
  "YER",
  "ZMW",
  "ZWL",
  "XBA",
  "XBB",
  "XBC",
  "XBD",
  "XTS",
  "XXX",
  "XAU",
  "XPD",
  "XPT",
  "XAG",
]);

const defaultCurrencySymbol = "$";
export const currencySymbols: Record<string, string> = {
  USD: "$", // United States Dollar
  EUR: "€", // Euro
  GBP: "£", // British Pound Sterling
  JPY: "¥", // Japanese Yen
  AUD: "A$", // Australian Dollar
  CAD: "C$", // Canadian Dollar
  CHF: "CHF", // Swiss Franc
  CNY: "¥", // Chinese Yuan
  SEK: "kr", // Swedish Krona
  NZD: "NZ$", // New Zealand Dollar
  INR: "₹", // Indian Rupee
  RUB: "₽", // Russian Ruble
  ZAR: "R", // South African Rand
  BRL: "R$", // Brazilian Real
  MXN: "Mex$", // Mexican Peso
  SGD: "S$", // Singapore Dollar
  HKD: "HK$", // Hong Kong Dollar
  NOK: "kr", // Norwegian Krone
  KRW: "₩", // South Korean Won
  TRY: "₺", // Turkish Lira
  AED: "د.إ", // United Arab Emirates Dirham
  ARS: "$", // Argentine Peso
  BGN: "лв", // Bulgarian Lev
  CLP: "$", // Chilean Peso
  COP: "$", // Colombian Peso
  CZK: "Kč", // Czech Koruna
  DKK: "kr", // Danish Krone
  EGP: "£", // Egyptian Pound
  HUF: "Ft", // Hungarian Forint
  IDR: "Rp", // Indonesian Rupiah
  ILS: "₪", // Israeli New Shekel
  MYR: "RM", // Malaysian Ringgit
  PHP: "₱", // Philippine Peso
  PLN: "zł", // Polish Zloty
  RON: "lei", // Romanian Leu
  SAR: "﷼", // Saudi Riyal
  THB: "฿", // Thai Baht
  VND: "₫", // Vietnamese Dong
  // Add more currencies as needed
};

export type Currency = keyof typeof currencySymbols;

export const getCurrencySymbol = (currencyCode: Currency) => currencySymbols[currencyCode] || defaultCurrencySymbol;

export function getStatusColor(quote: { status: string } | null, customer_decision_status?: string | null): string {
  if (customer_decision_status === CustomerDecisionStatus.SHORTLISTED) {
    return "bg-green-100";
  } else if (customer_decision_status === CustomerDecisionStatus.DECLINED) {
    return "bg-red-100";
  } else if (customer_decision_status === CustomerDecisionStatus.PENDING) {
    return "bg-yellow-100";
  }

  if (!quote) {
    return "bg-yellow-100";
  }

  switch (quote.status) {
    case "negotiated":
      return "bg-purple-100";
    case "estimated":
      return "bg-green-100";
    case "draft":
      return "bg-yellow-100";
    default:
      return "bg-gray-100";
  }
}

export enum ExploreHotelFeatures {
  RANDOM = "🎲 Random",
  BOUTIQUE = "🏩 Boutique",
  AFFORDABLE = "💲 Affordable",
  EMERGING = "🌱 Emerging",
  CHAIN = "🏢 Chain",
  WATERFRONT = "🌊 Waterfront",
  ACCESSIBLE = "♿ Accessible",
  MOUNTAINS = "🏔️ Mountains",
  MEETING_ROOMS = "📅 Meeting Rooms",
  SUBWAY = "🚇 Subway",
  PET_FRIENDLY = "🐾 Pet Friendly",
  POOL = "🏊 Pool",
  AV_CAPABILITIES = "🎥 AV Capabilities",
  HISTORICAL = "🏛️ Historical",
  ICONIC = "🗽 Iconic",
  TENNIS = "🎾 Tennis",
  GYM = "🏋️ Gym",
  WHIRLPOOL = "🛁 Whirlpool",
  CITY = "🏙️ City",
  GOLF_COURSE = "⛳ Golf Course",
  DANCE_FLOOR = "💃 Dance Floor",
  ONSITE_RESTAURANT = "🍽️ Onsite Restaurant",
  PIANO = "🎹 Piano",
  NEW = "✨ New",
}

/**
 *
 * @param value value to download
 * @param filename filename, must include extension as well
 *
 * Will open a popup where the user can download the dataString as filename
 */
export function downloadStringToFile(dataString: string, filename: string) {
  const stringToBytes = new Array(dataString.length);
  for (let i = 0; i < dataString.length; i++) {
    stringToBytes[i] = dataString.charCodeAt(i);
  }

  // export the data as CSV
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(new Blob([new Uint8Array(stringToBytes)]));
  link.setAttribute("download", filename);

  document.body.appendChild(link);
  link.click();
  link.parentNode!.removeChild(link);
}

/**
 * Fetches flight pricing data from the server.
 *
 * @param inputMethod - The method of input, e.g., "counts".
 * @param inputData - The input data string.
 * @param arrivalAirports - An array of arrival airport codes.
 * @param outboundDate - The outbound date as a string.
 * @param returnDate - The return date as a string, if applicable.
 * @param flightType - The type of flight, e.g., "1" for round trip.
 * @param travelClass - The travel class, e.g., "1" for economy.
 * @param stops - The number of stops, e.g., "0" for any.
 * @returns The response data or null if an error occurred.
 */
export async function fetchFlightPricing(
  supabase: SupabaseClient,
  inputMethod: string,
  inputData: string,
  arrivalAirports: string[],
  outboundDate: string,
  returnDate: string | undefined,
  flightType: string,
  travelClass: string,
  stops: number | null,
  eventId: string | null
): Promise<any | null> {
  try {
    const session = await supabase.auth.getSession();

    const response = await fetchPOSTNext(
      `/api/flights/get_pricing`,
      {
        inputMethod,
        inputData,
        arrivalAirports,
        outboundDate,
        returnDate,
        flightType,
        travelClass,
        stops,
        eventId,
      },
      {
        Authorization: `Bearer ${session.data.session?.access_token}`,
        "X-Supabase-Auth": JSON.stringify(session),
      }
    );

    if (!response) {
      console.error("No response from fetchFlightPricing");
      return null;
    }

    if (response.status !== 200) {
      console.error("Error fetching flight pricing:", response);
      return null;
    }

    const data = await response.json();

    console.log("data", data);

    return data || null;
  } catch (error) {
    console.error("Fetch error:", error);
    return null;
  }
}

/**
 * Force-regenerates the RFP draft after event details changed. Resolution,
 * generation, and persistence (body + subject + disclosure snapshot) run
 * server-side via `regenerateRfpDraftAction` — callers of this helper are
 * browser components, which must never resolve disclosure with their own role.
 * (The action authorizes the caller itself; no Supabase client is needed.)
 *
 * CONTRACT: the changed event details must already be PERSISTED — the action
 * regenerates from the stored row, not from this in-memory object (only
 * `event_id` is sent; the rest seeds the synthesized return value).
 *
 * @param event - The event whose changes are already saved to the DB
 * @returns The regenerated draft + a locally-synthesized updated event, or null on failure
 */
export const updateRfpDraft = async (
  event: Events
): Promise<{ parsedResponse: { new_draft: string; new_subject: string }; updatedEvent: Events | null } | null> => {
  const result = await regenerateRfpDraftAction(event.event_id, "rfp");
  if (!result.success) {
    console.error("updateRfpDraft: failed to regenerate RFP draft", { eventId: event.event_id }, result.error);
    return null;
  }

  return {
    parsedResponse: { new_draft: result.data.body, new_subject: result.data.subject },
    updatedEvent: { ...event, rfp_email_body: result.data.body, rfp_email_subject: result.data.subject },
  };
};

// Generic helper for updating email drafts in background
const updateEmailDraftInBackground = async <T extends { new_draft: string; new_subject?: string }>(
  event: Events,
  changes: Partial<Events>,
  draftExists: boolean,
  updateFn: (event: Events) => Promise<{ parsedResponse: T; updatedEvent: Events | null } | null>,
  draftType: "RFP" | "Rep email",
  errorType: string
): Promise<T | null> => {
  if (!draftExists || Object.keys(changes).length === 0) {
    return null;
  }

  try {
    const result = await updateFn(event);
    if (!result?.updatedEvent) {
      return null;
    }

    await notifyRfpDraftUpdated({ eventId: event.event_id, draftType, changes: { ...changes } }).catch((notifyError) =>
      console.error(`Error posting ${errorType} update notification:`, notifyError)
    );

    return result.parsedResponse;
  } catch (error) {
    console.error(`Error updating ${errorType}:`, error);
    return null;
  }
};

export const updateRfpDraftInBackground = async (
  event: Events,
  changes: Partial<Events>
): Promise<{ new_draft: string; new_subject: string } | null> => {
  return updateEmailDraftInBackground(event, changes, !!event.rfp_email_body, updateRfpDraft, "RFP", "RFP draft");
};

/**
 * Force-regenerates the chain-rep email draft after event details changed.
 * Same server-side resolution/persistence — and the same "changes must
 * already be persisted" contract — as {@link updateRfpDraft}.
 *
 * @param event - The event whose changes are already saved to the DB
 * @returns The regenerated draft + a locally-synthesized updated event, or null on failure
 */
export const updateRepEmailDraft = async (
  event: Events
): Promise<{ parsedResponse: { new_draft: string }; updatedEvent: Events | null } | null> => {
  const result = await regenerateRfpDraftAction(event.event_id, "rep");
  if (!result.success) {
    console.error(
      "updateRepEmailDraft: failed to regenerate rep email draft",
      { eventId: event.event_id },
      result.error
    );
    return null;
  }

  return {
    parsedResponse: { new_draft: result.data.body },
    updatedEvent: { ...event, chain_rep_email_body: result.data.body },
  };
};

export const updateRepEmailDraftInBackground = async (
  event: Events,
  changes: Partial<Events>
): Promise<{ new_draft: string } | null> => {
  return updateEmailDraftInBackground(
    event,
    changes,
    !!event.chain_rep_email_body,
    updateRepEmailDraft,
    "Rep email",
    "rep email draft"
  );
};

export async function fetchDirections(origin: string, destination: string, mode: string) {
  try {
    const response = await fetchPOSTNext("/api/maps/maps_directions", { origin, destination, mode });
    if (response && response.status === 200) {
      const data = await response.json();
      return data;
    } else {
      console.error("Failed to fetch directions:", response.statusText);
    }
  } catch (error) {
    console.error("Error fetching directions:", error);
  }
}

// Helper to determine if a venue is visible (has green eyeball)
export const isVenueWithMetadataVisible = (venue: StaticVenueWithMetadata): boolean => {
  return (
    !(venue.candidate.archived ?? false) &&
    !(venue.candidate.customer_removed ?? false) &&
    (venue.candidate.show_on_dashboard ?? false)
  );
};

// Helper function to chunk array into smaller arrays
function chunkArray<T>(array: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, i) => array.slice(i * size, i * size + size));
}

interface RfpEmailPayload {
  event_id: number;
  venues: StaticVenue[];
  from: string;
  from_name?: string;
  subject: string;
  htmlContent?: string;
  draft?: string;
  chainReps?: HotelChainRep[];
  toOverride?: string[];
  cc?: string | string[];
  bcc?: string | string[];
  attachments?: EmailAttachment[];
  // Staff confirmed they want to send despite the event being flagged as a test
  // event. Bypasses the route's test-event chokepoint. Ignored for non-test events.
  allowTestEvent?: boolean;
}

export const sendRfpTableEmail = async (payload: RfpEmailPayload): Promise<Response> => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  // Forward cookies for server-side calls (browser handles cookies automatically on client)
  try {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    headers.Cookie = cookieStore
      .getAll()
      .map((c) => `${c.name}=${c.value}`)
      .join("; ");
  } catch {
    // Client-side — fetch includes cookies automatically
  }

  const response = await fetch(getApiUrl("/api/email/send_rfp_email"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Failed to send email");
  }

  return response;
};

// `sendTableEmail` and its payload types moved to src/lib/email/send-table-email.ts so surfaces
// that only need to send an email don't pull in this module's whole graph. Re-exported here so
// existing importers are unchanged.
export { sendTableEmail };
export type { EmailAttachment, TableEmailPayload };

// NOTE: the legacy RFP/rep draft-table builders (createTableRows,
// createRFPDraftTable, createRepEmailDraftTable) moved to
// src/lib/rfp/draft-tables.ts — draft regeneration (src/lib/rfp/draft.ts)
// is their only consumer.

function convertHtmlToPlainText(html: string): string {
  // First, handle table structures
  const tableProcessed = html
    // Convert table rows to sections
    .replace(/<tr[^>]*>/gi, "")
    .replace(/<\/tr>/gi, "\n")
    // Convert th/td content to key-value format
    .replace(/<th[^>]*>(.*?)<\/th>/gi, "$1")
    .replace(/<td[^>]*>(.*?)<\/td>/gi, "$1")
    // Remove table structure
    .replace(/<table[^>]*>/gi, "")
    .replace(/<\/table>/gi, "\n")
    // Convert strong tags to asterisks
    .replace(/<strong>(.*?)<\/strong>/gi, "*$1*");

  // Then apply our standard HTML cleanup
  const blockSpaced = tableProcessed
    // Replace common HTML entities (decode &amp; last to avoid double-unescaping)
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    // Replace block elements with appropriate spacing
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n");

  return (
    // Remove all remaining HTML tags (looped so nested input can't re-form a tag)
    stripHtmlTags(blockSpaced)
      // Fix multiple newlines and spaces
      .replace(/\n\s*\n\s*\n/g, "\n\n")
      .replace(/[ \t]+/g, " ")
      // Add bullet points to key-value pairs
      .replace(/^([^:]+):(.+)$/gm, "• $1:$2")
      // Trim whitespace
      .trim()
  );
}
